import { BankVerificationSource } from "client/services/form";

import { MerchantValidationError } from "../error";
import * as Fields from "../fields";
import { FieldSet } from "../fieldset";
import { Merchant } from "../merchant";
import { SignatoryType } from "../types";

export type BankFieldSetNames = "PrimaryBankFieldSet" | "SecondaryBankFieldSet";

export type BankFieldSetValueMap = {
  account: string;
  account2: string;
  bankAccountName: string;
  bankAccountName2: string;
  bankBranch: string;
  bankBranch2: string;
  bankName: string;
  bankName2: string;
  bsb: string;
  bsb2: string;
  canadaChequingAccount: number;
  canadaRoutingInstitutionIdNumber: string;
  canadaRoutingTransitIdNumber: string;
  confirmAccount: string;
  confirmAccount2: string;
  confirmCanadaChequingAccount: number;
  directDebitAgreement: string;
  directDebitAgreement2: string;
  email: string;
  email2: string;
  routing: string;
  routing2: string;
  signatoryName: string;
  signatoryName2: string;
  signatoryType: SignatoryType;
  signatoryType2: SignatoryType;
  sortCode: string;
  sortCode2: string;
};

export type BankFieldSetValueMapper<T> = {
  [K in keyof BankFieldSetValueMap]: T;
};

export class BankFieldSet extends FieldSet<BankFieldSetValueMap> {
  static fromJson(
    Bank: BankFieldSet,
    {
      account,
      account2,
      bankAccountName,
      bankAccountName2,
      bankBranch,
      bankBranch2,
      bankName,
      bankName2,
      bsb,
      bsb2,
      canadaChequingAccount,
      canadaRoutingInstitutionIdNumber,
      canadaRoutingTransitIdNumber,
      confirmAccount,
      confirmAccount2,
      confirmCanadaChequingAccount,
      directDebitAgreement,
      directDebitAgreement2,
      email,
      email2,
      routing,
      routing2,
      signatoryName,
      signatoryName2,
      signatoryType,
      signatoryType2,
      sortCode,
      sortCode2,
    }: Partial<BankFieldSetValueMap>
  ): BankFieldSet {
    Bank.account.value = account;
    Bank.account2.value = account2;
    Bank.bankAccountName.value = bankAccountName;
    Bank.bankAccountName2.value = bankAccountName2;
    Bank.bankBranch.value = bankBranch;
    Bank.bankBranch2.value = bankBranch2;
    Bank.bankName.value = bankName;
    Bank.bankName2.value = bankName2;
    Bank.bsb.value = bsb;
    Bank.bsb2.value = bsb2;
    Bank.canadaChequingAccount.value = canadaChequingAccount;
    Bank.canadaRoutingInstitutionIdNumber.value = canadaRoutingInstitutionIdNumber;
    Bank.canadaRoutingTransitIdNumber.value = canadaRoutingTransitIdNumber;
    Bank.confirmAccount.value = confirmAccount;
    Bank.confirmAccount2.value = confirmAccount2;
    Bank.confirmCanadaChequingAccount.value = confirmCanadaChequingAccount;
    Bank.directDebitAgreement.value = directDebitAgreement;
    Bank.directDebitAgreement2.value = directDebitAgreement2;
    Bank.email.value = email;
    Bank.email2.value = email2;
    Bank.routing.value = routing;
    Bank.routing2.value = routing2;
    Bank.signatoryName.value = signatoryName;
    Bank.signatoryName2.value = signatoryName2;
    Bank.signatoryType.value = signatoryType;
    Bank.signatoryType2.value = signatoryType2;
    Bank.sortCode.value = sortCode;
    Bank.sortCode2.value = sortCode2;

    return Bank;
  }

  public fieldSetName: BankFieldSetNames;
  public account: Fields.AccountField;
  public account2: Fields.AccountField;
  public bankAccountName: Fields.BankAccountNameField;
  public bankAccountName2: Fields.BankAccountNameField;
  public bankBranch: Fields.BankBranchField;
  public bankBranch2: Fields.BankBranchField;
  public bankName: Fields.BankNameField;
  public bankName2: Fields.BankNameField;
  public bsb: Fields.BsbField;
  public bsb2: Fields.BsbField;
  public canadaChequingAccount: Fields.CanadaChequingAccountField;
  public canadaRoutingInstitutionIdNumber: Fields.CanadaRoutingInstitutionIdField;
  public canadaRoutingTransitIdNumber: Fields.CanadaRoutingField;
  public confirmAccount: Fields.ConfirmAccountField;
  public confirmAccount2: Fields.ConfirmAccountField;
  public confirmCanadaChequingAccount: Fields.ConfirmCanadaChequingAccountField;
  public directDebitAgreement: Fields.DirectDebitAgreementField;
  public directDebitAgreement2: Fields.DirectDebitAgreementField;
  public email: Fields.EmailField;
  public email2: Fields.EmailField;
  public routing: Fields.RoutingField;
  public routing2: Fields.RoutingField;
  public signatoryName: Fields.SignatoryNameField;
  public signatoryName2: Fields.SignatoryNameField;
  public signatoryType: Fields.SignatoryTypeField;
  public signatoryType2: Fields.SignatoryTypeField;
  public sortCode: Fields.SortCodeField;
  public sortCode2: Fields.SortCodeField;

  public verificationSource: BankVerificationSource;

  constructor(model: Merchant, fieldSetName: BankFieldSetNames) {
    super();

    this.fieldSetName = fieldSetName;
    this.verificationSource = "MANUAL";

    this.account = new Fields.AccountField(model, {
      propertyName: "account",
    });
    this.account2 = new Fields.AccountField(model, {
      propertyName: "account2",
    });
    this.bankAccountName = new Fields.BankAccountNameField(model, {
      propertyName: "bankAccountName",
    });
    this.bankAccountName2 = new Fields.BankAccountNameField(model, {
      propertyName: "bankAccountName2",
    });
    this.bankBranch = new Fields.BankBranchField(model, {
      propertyName: "bankBranch",
    });
    this.bankBranch2 = new Fields.BankBranchField(model, {
      propertyName: "bankBranch2",
    });
    this.bankName = new Fields.BankNameField(model, {
      propertyName: "bankName",
    });
    this.bankName2 = new Fields.BankNameField(model, {
      propertyName: "bankName2",
    });
    this.bsb = new Fields.BsbField(model, {
      propertyName: "bsb",
    });
    this.bsb2 = new Fields.BsbField(model, {
      propertyName: "bsb2",
    });
    this.canadaChequingAccount = new Fields.CanadaChequingAccountField(model);
    this.canadaRoutingInstitutionIdNumber = new Fields.CanadaRoutingInstitutionIdField(
      model
    );
    this.canadaRoutingTransitIdNumber = new Fields.CanadaRoutingField(model);
    this.confirmAccount = new Fields.ConfirmAccountField(model, {
      propertyName: "confirmAccount",
    });
    this.confirmAccount2 = new Fields.ConfirmAccountField(model, {
      propertyName: "confirmAccount2",
    });
    this.confirmCanadaChequingAccount = new Fields.ConfirmCanadaChequingAccountField(
      model
    );
    this.directDebitAgreement = new Fields.DirectDebitAgreementField(model, {
      propertyName: "directDebitAgreement",
    });
    this.directDebitAgreement2 = new Fields.DirectDebitAgreementField(model, {
      propertyName: "directDebitAgreement2",
    });
    this.email = new Fields.EmailField(model, {
      propertyName: "email",
    });
    this.email2 = new Fields.EmailField(model, {
      propertyName: "email2",
    });
    this.routing = new Fields.RoutingField(model, {
      propertyName: "routing",
    });
    this.routing2 = new Fields.RoutingField(model, {
      propertyName: "routing2",
    });
    this.signatoryName = new Fields.SignatoryNameField(model, {
      propertyName: "signatoryName",
    });
    this.signatoryName2 = new Fields.SignatoryNameField(model, {
      propertyName: "signatoryName2",
    });
    this.signatoryType = new Fields.SignatoryTypeField(model, {
      propertyName: "signatoryType",
    });
    this.signatoryType2 = new Fields.SignatoryTypeField(model, {
      propertyName: "signatoryType2",
    });
    this.sortCode = new Fields.SortCodeField(model, {
      propertyName: "sortCode",
    });
    this.sortCode2 = new Fields.SortCodeField(model, {
      propertyName: "sortCode2",
    });

    if (fieldSetName === "PrimaryBankFieldSet") {
      this.fields = [
        this.account,
        this.bankAccountName,
        this.bankBranch,
        this.bankName,
        this.bsb,
        this.canadaChequingAccount,
        this.canadaRoutingInstitutionIdNumber,
        this.canadaRoutingTransitIdNumber,
        this.confirmAccount,
        this.confirmCanadaChequingAccount,
        this.directDebitAgreement,
        this.email,
        this.routing,
        this.signatoryName,
        this.signatoryType,
        this.sortCode,
      ];
    } else {
      this.fields = [
        this.account2,
        this.bankAccountName2,
        this.bankBranch2,
        this.bankName2,
        this.bsb2,
        this.confirmAccount2,
        this.directDebitAgreement2,
        this.email2,
        this.routing2,
        this.signatoryName2,
        this.signatoryType2,
        this.sortCode2,
      ];
    }
  }

  validate({ stringify = true }: { stringify: boolean }): void {
    const Errors = new MerchantValidationError();

    let errorCount = 0;
    for (const field of this.fields) {
      try {
        field.validate();
      } catch (ex) {
        errorCount++;

        if (
          this.fieldSetName === "PrimaryBankFieldSet" &&
          Errors.fieldErrorMap.primaryBank
        ) {
          Errors.fieldErrorMap.primaryBank[
            field.propertyName as keyof BankFieldSetValueMap
          ] = { error: stringify ? ex.message : ex };
        } else if (
          this.fieldSetName === "SecondaryBankFieldSet" &&
          Errors.fieldErrorMap.secondaryBank
        ) {
          Errors.fieldErrorMap.secondaryBank[
            field.propertyName as keyof BankFieldSetValueMap
          ] = { error: stringify ? ex.message : ex };
        }
      }

      if (errorCount) {
        throw Errors.fieldErrorMap;
      }
    }
  }
}
